// hooks/administration/entreprises/useEntreprises.ts
import { useState, useCallback } from "react";
import useToken from "../../../hooks/features/auth/useToken";
import { Entreprise, EntrepriseStats } from "../../../types/features/Entreprise";
import { entrepriseService } from "../../../services//entrepriseService";

export default function useEntreprises() {
  const { token } = useToken();
  const [entreprises, setEntreprises] = useState<Entreprise[]>([]);
  const [selectedEntreprise, setSelectedEntreprise] = useState<Entreprise | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });
  const [stats, setStats] = useState<EntrepriseStats | null>(null);

  const getEntreprises = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await entrepriseService.getEntreprises(token, page, perPage);
      setEntreprises(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des entreprises");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getEntrepriseById = useCallback(async (id: string): Promise<Entreprise | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const entreprise = await entrepriseService.getEntrepriseById(token, id);
      setSelectedEntreprise(entreprise);
      return entreprise;
    } catch (err) {
      setError("Erreur lors du chargement de l'entreprise");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getMyEntreprise = useCallback(async (): Promise<Entreprise | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const entreprise = await entrepriseService.getMyEntreprise(token);
      setSelectedEntreprise(entreprise);
      return entreprise;
    } catch (err) {
      setError("Erreur lors du chargement de votre entreprise");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createEntreprise = useCallback(async (data: Omit<Entreprise, "id" | "created_at" | "updated_at">): Promise<Entreprise | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const newEntreprise = await entrepriseService.createEntreprise(token, data);
      setEntreprises((prev) => [newEntreprise, ...prev]);
      return newEntreprise;
    } catch (err) {
      setError("Erreur lors de la création de l'entreprise");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateEntreprise = useCallback(async (id: string, data: Partial<Entreprise>): Promise<Entreprise | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const updatedEntreprise = await entrepriseService.updateEntreprise(token, id, data);
      setEntreprises((prev) => prev.map((e) => (e.id === id ? updatedEntreprise : e)));
      if (selectedEntreprise?.id === id) setSelectedEntreprise(updatedEntreprise);
      return updatedEntreprise;
    } catch (err: any) {
      setError("Erreur lors de la mise à jour de l'entreprise");
      console.error(err?.response?.data);
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedEntreprise, token]);

  const deleteEntreprise = useCallback(async (id: string): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await entrepriseService.deleteEntreprise(token, id);
      setEntreprises((prev) => prev.filter((e) => e.id !== id));
      if (selectedEntreprise?.id === id) setSelectedEntreprise(null);
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de l'entreprise");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [selectedEntreprise, token]);

  const getEntrepriseAgents = useCallback(async (id: string, page: number = 1, perPage: number = 20) => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await entrepriseService.getEntrepriseAgents(token, id, page, perPage);
      return response;
    } catch (err) {
      setError("Erreur lors du chargement des agents");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getEntrepriseStats = useCallback(async (id: string) => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const data = await entrepriseService.getEntrepriseStats(token, id);
      setStats(data);
      return data;
    } catch (err) {
      setError("Erreur lors du chargement des statistiques");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  return {
    entreprises,
    selectedEntreprise,
    loading,
    error,
    pagination,
    stats,
    getEntreprises,
    getEntrepriseById,
    getMyEntreprise,
    createEntreprise,
    updateEntreprise,
    deleteEntreprise,
    getEntrepriseAgents,
    getEntrepriseStats,
  };
}